/**
 * Sprint P32 · Drillhole trace layer.
 * Thick lines from collar → toe with optional outlier filtering.
 */
import { useMemo } from "react";
import * as THREE from "three";
import { Line } from "@react-three/drei";
import { worldToScene, isValidCoordinate, pointInBox } from "@/lib/sceneCoords";


export function DrillholeTraces({
  holes, showTraces, origin, verticalExaggeration,
  activeBBox, includeOutliers = true,
  collarRadius = 12, onBounds, lineWidth = 4,
}) {
  const local = useMemo(() => {
    if (!showTraces || !holes?.length || !origin) return [];
    const out = [];
    const box = new THREE.Box3();
    for (const h of holes) {
      const pts = [];
      let collarInside = false;
      for (let k = 0; k < h.x.length; k++) {
        if (!isValidCoordinate(h.x[k], h.y[k], h.z[k])) continue;
        const p = worldToScene(h.x[k], h.y[k], h.z[k], origin, verticalExaggeration);
        pts.push(p);
      }
      if (pts.length < 2) continue;
      if (activeBBox && !includeOutliers) {
        collarInside = pts.some((p) => pointInBox(p, activeBBox));
        if (!collarInside) continue;
      }
      pts.forEach((p) => box.expandByPoint(p));
      out.push({ id: h.hole_id, pts, collar: pts[0] });
    }
    onBounds?.(box, { count: out.length });
    return out;
  }, [holes, showTraces, origin, verticalExaggeration, activeBBox, includeOutliers, onBounds]);

  if (!showTraces || !local.length) return null;
  return (
    <group name="drillhole-traces"
           userData={{ coordinateSpace: "local_scene", layer: "traces" }}>
      {local.map((h, i) => (
        <Line key={`${h.id}-${i}`} points={h.pts} color="#F59E0B"
               lineWidth={lineWidth} transparent opacity={0.95} />
      ))}
    </group>
  );
}
